import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { BillsPaymentService, AirtimeService, DataService, CableService, ElectricityService } from '../../services/bills-payment.service';
import { AuthService } from '../../services/auth.service';
import { SettingsService } from '../../services/settings.service';
import { BillsPurchaseModalComponent } from './bills-purchase-modal/bills-purchase-modal.component';

export interface BillType {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
}

@Component({
  selector: 'app-bills-payment',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    ProgressSpinnerModule,
    ToastModule,
    BillsPurchaseModalComponent
  ],
  providers: [MessageService],
  templateUrl: './bills-payment.component.html',
  styleUrl: './bills-payment.component.css'
})
export class BillsPaymentComponent implements OnInit {
  platformId = signal<string>('');
  platformName = signal<string>('');
  isLoading = signal(false);
  isLoggedIn = signal(false);
  
  // Bill types
  billTypes: BillType[] = [
    {
      id: 'airtime',
      name: 'Airtime',
      icon: 'phone',
      color: 'yellow',
      description: 'Recharge your mobile phone'
    },
    {
      id: 'data',
      name: 'Data',
      icon: 'wifi',
      color: 'blue',
      description: 'Buy data bundles'
    },
    {
      id: 'cable',
      name: 'Cable TV',
      icon: 'tv',
      color: 'pink',
      description: 'Pay for cable subscription'
    },
    {
      id: 'electricity',
      name: 'Electricity',
      icon: 'bolt',
      color: 'green',
      description: 'Buy electricity units'
    }
  ];

  // Services data
  airtimeServices = signal<AirtimeService[]>([]);
  dataServices = signal<DataService[]>([]);
  cableServices = signal<CableService[]>([]);
  electricityServices = signal<ElectricityService[]>([]);

  // Modal state
  showPurchaseModal = signal(false);
  selectedBillType = signal<BillType | null>(null);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private billsPaymentService: BillsPaymentService,
    private settingsService: SettingsService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    // Get platform ID and name from route
    this.route.params.subscribe(params => {
      const id = params['id'];
      let name = params['name'] || 'Store';
      
      if (name && name !== 'Store') {
        try {
          name = decodeURIComponent(name);
        } catch (e) {
          console.error('Error decoding platform name:', e);
        }
      }
      
      this.platformId.set(id);
      this.platformName.set(name);
      
      // Check authentication
      this.isLoggedIn.set(this.authService.isAuthenticated());
      
      if (this.isLoggedIn()) {
        this.loadServices();
      }
    });
  }

  loadServices(): void {
    this.isLoading.set(true);
    const platformId = this.platformId();
    
    // Load all services in parallel
    Promise.all([
      this.billsPaymentService.getAirtimeServices(platformId).toPromise(),
      this.billsPaymentService.getDataServices(platformId).toPromise(),
      this.billsPaymentService.getCableServices(platformId).toPromise(),
      this.billsPaymentService.getElectricityServices(platformId).toPromise()
    ]).then(([airtimeResult, dataResult, cableResult, electricityResult]) => {
      if (airtimeResult?.success) {
        this.airtimeServices.set(airtimeResult.data);
      }
      if (dataResult?.success) {
        this.dataServices.set(dataResult.data);
      }
      if (cableResult?.success) {
        this.cableServices.set(cableResult.data);
      }
      if (electricityResult?.success) {
        this.electricityServices.set(electricityResult.data);
      }
      this.isLoading.set(false);
    }).catch(error => {
      console.error('Error loading services:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load bill payment services',
        life: 5000
      });
      this.isLoading.set(false);
    });
  }

  openPurchaseModal(billType: BillType): void {
    if (!this.isLoggedIn()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Authentication Required',
        detail: 'Please sign in to pay bills',
        life: 3000
      });
      this.router.navigate(['/signin'], { queryParams: { returnUrl: this.router.url } });
      return;
    }

    this.selectedBillType.set(billType);
    this.showPurchaseModal.set(true);
  }

  closePurchaseModal(): void {
    this.showPurchaseModal.set(false);
    this.selectedBillType.set(null);
  }

  onPurchaseSuccess(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Payment Successful',
      detail: 'Your bill payment has been processed successfully!',
      life: 5000
    });
    this.closePurchaseModal();
    // Optionally reload services or shopping card balance
  }

  goBack(): void {
    const platformId = this.platformId();
    const platformName = this.platformName();
    
    if (platformId && platformName) {
      const encodedName = encodeURIComponent(platformName);
      this.router.navigate(['/store', platformId, encodedName]);
    } else {
      this.router.navigate(['/']);
    }
  }

  goToSignIn(): void {
    this.router.navigate(['/signin'], { queryParams: { returnUrl: this.router.url } });
  }
}
